import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Drawer, Table } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { Building2 } from 'lucide-react';
import { parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { useMemo, useState } from 'react';
import {
  ORG_UNIT_SORT_FIELDS,
  actingOrgUnitIdOf,
  type OrgUnitDto,
  type OrgUnitSortField,
  type Paginated,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { ElevationMark } from '@/components/elevation/ElevationMark';
import { HierarchyTable } from '@/components/HierarchyTable';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { CountCell } from './components/CountCell';
import { MoveUnitModal } from './components/MoveUnitModal';
import { OrgUnitDetail } from './components/OrgUnitDetail';
import { OrgUnitDrawer } from './components/OrgUnitDrawer';
import { OrgUnitToolbar } from './components/OrgUnitToolbar';
import { useAuth } from '@/hooks/useAuth';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useOrgUnitCounts } from '@/hooks/useOrgUnitCounts';
import { useServerTable } from '@/hooks/useServerTable';
import { useTreeExpansion } from '@/hooks/useTreeExpansion';
import { can, elevatedNodeRefusal } from '@/lib/accessRole';
import { ORG_UNIT_TYPE_LABEL, orgUnitKindLabel } from '@/lib/labels';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import {
  buildOrgForest,
  expandableKeysOf,
  keysToDepth,
  ORG_TREE_MAX_PAGES,
  ORG_TREE_PAGE_LIMIT,
  toOrgTreeRows,
  type OrgTreeRow,
  sortTerritorialFirst,
} from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE ORGANISATION HIERARCHY.
 *
 *  A TABLE whose first column is a tree, not a tree with a details panel bolted on: Type,
 *  Short code, Officers, Nodals and Status are ordinary columns, aligned at every depth.
 *  The counts are the point — a tree showing only names tells an operator nothing they
 *  could not already guess.
 *
 *  ─── TWO MODES, ONE TOOLBAR ────────────────────────────────────────────────────────
 *  TREE mode is the default and uses `GET /org-units/tree`, which returns the whole
 *  scoped subtree flat and unsorted. The moment a search or a type filter is active the
 *  screen switches to FLAT mode over `GET /org-units`, driven by `useServerTable`.
 *
 *  That is not a workaround for the endpoint split — it is the correct UI. A SEARCH
 *  RESULT IS A LIST, NOT A TREE: rendering "units matching 'tolly'" as a hierarchy would
 *  mean inventing ancestors the server did not return, and the server offers no way to
 *  fetch them. Flat mode is also `useServerTable`'s second consumer.
 *
 *  ─── The tree is fetched with `includeInactive: true`, ALWAYS ──────────────────────
 *  Two reasons, and the second is a correctness one. The status filter becomes instant
 *  rather than a refetch; and `/officers` returns officers in ANY in-scope unit including
 *  deactivated ones, so a tree missing those units would silently drop their officers out
 *  of every roll-up. See `useOrgUnitCounts`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * [amended P1b] `shortCode` is GONE from this list, and the chip inside the tree cell is
 * now the only place a unit's code appears. P1a.2 shipped both because the brief listed
 * both, and recorded it as D6 — "the one column worth reconsidering". It has been
 * reconsidered: hiding it was possible, but a duplicate that every operator has to hide
 * by hand is not a feature, and colour and code both meaning the same thing twice in one
 * row is the redundancy the design language exists to remove.
 */
const COLUMN_KEYS = ['type', 'officers', 'nodals', 'status'] as const;
type ColumnKey = (typeof COLUMN_KEYS)[number];

const COLUMN_LABELS: Record<ColumnKey, string> = {
  type: 'Type',
  officers: 'Officers',
  nodals: 'Nodals',
  status: 'Status',
};

const OrgTreePage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; unit?: OrgUnitDto; parent?: OrgUnitDto }>();
  const [moving, setMoving] = useState<OrgUnitDto | undefined>();
  const [selectedId, setSelectedId] = useQueryState(
    'unit',
    parseAsString.withOptions({ history: 'replace', clearOnDefault: true }),
  );
  const [status, setStatus] = useState<'active' | 'all'>('active');

  const canWrite = officer ? can(officer.accessRole, 'org.write') : false;
  /* [C5c §11h] The unit's nodal access level is set here, and only by `org.elevate` (SUPER_ADMIN). */
  const canElevate = officer ? can(officer.accessRole, 'org.elevate') : false;
  const canReparent = officer ? can(officer.accessRole, 'org.reparent') : false;

  const columns = useColumnVisibility<ColumnKey>({ keys: COLUMN_KEYS });

  /**
   * The tree.
   *
   * `includeInactive: true` ALWAYS — see the file header; it is a correctness requirement
   * for the counts, not a convenience for the status filter.
   *
   * Page one first, then however many more the envelope says exist, bounded by
   * `ORG_TREE_MAX_PAGES`. Two calls rather than one so the later pages can be gated on a
   * number that only exists after the first has landed — and bounded rather than
   * unbounded, mirroring the API's own `MAX_SUBTREE_NODES` tripwire: past the cap the
   * screen says what it is showing and points at search.
   */
  const firstPageQuery = { page: 1, limit: ORG_TREE_PAGE_LIMIT, includeInactive: true };
  const firstPage = useQuery({
    queryKey: queryKeys.orgUnits.tree(undefined, firstPageQuery),
    queryFn: ({ signal }) => api.get<Paginated<OrgUnitDto>>('/org-units/tree', { query: firstPageQuery, signal }),
    staleTime: 30_000,
  });

  const extraPageCount = Math.min(Math.max((firstPage.data?.pages ?? 1) - 1, 0), ORG_TREE_MAX_PAGES - 1);
  const extraPages = useQueries({
    queries: Array.from({ length: extraPageCount }, (_value, index) => {
      const query = { page: index + 2, limit: ORG_TREE_PAGE_LIMIT, includeInactive: true };
      return {
        queryKey: queryKeys.orgUnits.tree(undefined, query),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          api.get<Paginated<OrgUnitDto>>('/org-units/tree', { query, signal }),
        staleTime: 30_000,
      };
    }),
  });

  const allUnits = useMemo(
    () => [...(firstPage.data?.items ?? []), ...extraPages.flatMap((page) => page.data?.items ?? [])],
    [firstPage.data, extraPages],
  );

  /* [C5c §11] The name of the unit a row's nodal officers act at, from the tree already loaded —
     `actingOrgUnitIdOf` is the one derivation, the server's own. Absent from the tree, it is omitted. */
  const unitNameById = useMemo(() => new Map(allUnits.map((unit) => [unit.id, unit.name])), [allUnits]);
  const actingAtOf = (unit: Pick<OrgUnitDto, 'id' | 'ancestors' | 'elevation'>): string | undefined =>
    unitNameById.get(actingOrgUnitIdOf({ _id: unit.id, ancestors: unit.ancestors, elevation: unit.elevation }));

  const treeTotal = firstPage.data?.total ?? 0;
  const treeLoading = firstPage.isLoading || extraPages.some((page) => page.isLoading);
  const truncatedTree = treeTotal > allUnits.length;

  const visibleUnits = useMemo(
    () => (status === 'all' ? allUnits : allUnits.filter((unit: OrgUnitDto) => unit.isActive)),
    [allUnits, status],
  );

  /*
   * ⚠️ [amended WINGS.1] `sortTerritorialFirst` GOES ON THE INPUT, AND ON `visibleUnits`
   * ALONE. On the input, because `toOrgTreeRows` derives `isLastChild` from array position
   * and that draws the guide rail — read the helper's header. On `visibleUnits` rather than
   * `allUnits`, because `allUnits` also feeds `useOrgUnitCounts` and the `selected` lookup
   * below, neither of which wants a new order and one of which is a map.
   */
  const rows = useMemo(() => toOrgTreeRows(buildOrgForest(sortTerritorialFirst(visibleUnits))), [visibleUnits]);

  const expansion = useTreeExpansion({
    expandableKeys: useMemo(() => expandableKeysOf(rows), [rows]),
    autoKeys: useMemo(() => keysToDepth(rows, 2), [rows]),
    boundedKeys: useMemo(() => keysToDepth(rows, 3), [rows]),
  });

  const counts = useOrgUnitCounts({ units: allUnits, enabled: allUnits.length > 0 });

  /**
   * FLAT MODE IS DERIVED, NOT TOGGLED.
   *
   * The URL is the source of truth for both keys, and `useServerTable` owns writing them
   * — but its `enabled` has to be known BEFORE the hook is constructed. Reading the same
   * two keys through nuqs is not a second state store: it is the same store, read twice.
   */
  const [urlFilters] = useQueryStates({ search: parseAsString.withDefault(''), type: parseAsString.withDefault('') });
  const isFlat = Boolean(urlFilters.search.trim() || urlFilters.type);

  // ── FLAT MODE — useServerTable's second consumer ──────────────────────────────────
  const flat = useServerTable<OrgUnitDto, OrgUnitSortField>({
    sortFields: ORG_UNIT_SORT_FIELDS,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
    filterKeys: ['type'],
    queryKey: (params) => queryKeys.orgUnits.list({ ...params, includeInactive: status === 'all' }),
    queryFn: (params, signal) =>
      api.get<Paginated<OrgUnitDto>>('/org-units', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          includeInactive: status === 'all',
          ...params.filters,
        },
        signal,
      }),
    enabled: isFlat,
  });

  const selected = selectedId ? allUnits.find((unit: OrgUnitDto) => unit.id === selectedId) : undefined;

  const deactivate = useMutation({
    mutationFn: (id: string) => api.post<OrgUnitDto>(`/org-units/${id}/deactivate`),
    onSuccess: () => {
      void message.success('Org unit deactivated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.orgUnits.all });
    },
    // The API returns 409 with the count of what still references the node. Showing the
    // reason turns a dead end into the next step.
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmDeactivate = (unit: OrgUnitDto) =>
    modal.confirm({
      title: `Deactivate ${unit.name}?`,
      content:
        'Deactivation is used instead of deletion. It is refused while the unit still has active child units or ' +
        'officers posted to it.',
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      onOk: () => void deactivate.mutateAsync(unit.id),
    });

  const rowActions = (unit: OrgUnitDto): MenuProps['items'] => [
    { key: 'open', label: 'Open details', onClick: () => void setSelectedId(unit.id) },
    {
      key: 'add-child',
      label: 'Add child unit',
      disabled: !canWrite,
      onClick: () => setEditing({ mode: 'create', parent: unit }),
    },
    /* [C5c §11c] The node an elevated caller acts at is visible and unchangeable. */
    {
      key: 'edit',
      label: 'Edit unit',
      disabled: !canWrite || elevatedNodeRefusal(officer, unit.id) !== undefined,
      onClick: () => setEditing({ mode: 'edit', unit }),
    },
    ...(canReparent ? [{ key: 'move', label: 'Move…', onClick: () => setMoving(unit) }] : []),
    { type: 'divider' as const },
    {
      key: 'deactivate',
      label: 'Deactivate',
      danger: true,
      disabled: !canWrite || !unit.isActive || elevatedNodeRefusal(officer, unit.id) !== undefined,
      onClick: () => void confirmDeactivate(unit),
    },
  ];

  const dataColumns = [
    columns.isVisible('type') && {
      key: 'type',
      title: COLUMN_LABELS.type,
      width: 150,
      render: (_value: unknown, row: OrgTreeRow) => (
        <span className="text-sm text-muted-foreground">{orgUnitKindLabel(row.type, row.subType)}</span>
      ),
    },
    columns.isVisible('officers') && {
      key: 'officers',
      title: COLUMN_LABELS.officers,
      width: 110,
      align: 'right' as const,
      render: (_value: unknown, row: OrgTreeRow) => (
        <CountCell value={counts.countsFor(row.key)?.officersOwn} loading={counts.isLoading} />
      ),
    },
    columns.isVisible('nodals') && {
      key: 'nodals',
      title: COLUMN_LABELS.nodals,
      width: 100,
      align: 'right' as const,
      render: (_value: unknown, row: OrgTreeRow) => (
        <CountCell value={counts.countsFor(row.key)?.nodalsOwn} loading={counts.isLoading} />
      ),
    },
    columns.isVisible('status') && {
      key: 'status',
      title: COLUMN_LABELS.status,
      width: 130,
      render: (_value: unknown, row: OrgTreeRow) =>
        row.isActive ? <StatusDot tone="success" label="Active" /> : <StatusDot tone="neutral" label="Inactive" />,
    },
  ].filter(Boolean) as NonNullable<TableProps<OrgTreeRow>['columns']>;

  const flatColumns: TableProps<OrgUnitDto>['columns'] = [
    {
      title: 'Unit',
      key: 'name',
      sorter: true,
      defaultSortOrder: 'ascend',
      render: (_value, row) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
            {row.shortCode}
          </span>
          <span className="truncate">{row.name}</span>
          <ElevationMark elevation={row.elevation} actingAt={actingAtOf(row)} />
        </span>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      render: (_value, row) => <span className="text-muted-foreground">{ORG_UNIT_TYPE_LABEL[row.type]}</span>,
    },
    { title: 'Depth', dataIndex: 'depth', key: 'depth', sorter: true, align: 'right', width: 100 },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_value, row) =>
        row.isActive ? <StatusDot tone="success" label="Active" /> : <StatusDot tone="neutral" label="Inactive" />,
    },
  ];

  const columnItems: MenuProps['items'] = COLUMN_KEYS.map((key) => ({
    key,
    label: `${columns.isVisible(key) ? '✓ ' : '　'}${COLUMN_LABELS[key]}`,
    onClick: () => columns.toggle(key),
  }));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={Building2}
        title="Organisation"
        subtitle={`${visibleUnits.length} unit${visibleUnits.length === 1 ? '' : 's'} within your scope`}
        filters={
          <OrgUnitToolbar
            search={flat.searchInput}
            onSearchChange={flat.onSearchChange}
            type={flat.params.filters.type}
            onTypeChange={(value) => flat.setFilter('type', value)}
            status={status}
            onStatusChange={setStatus}
            columnItems={columnItems}
            onExpandAll={expansion.expandAll}
            onCollapseAll={expansion.collapseAll}
            showExpansion={!isFlat}
            canWrite={canWrite}
            onCreate={() => setEditing({ mode: 'create' })}
          />
        }
      />

      {truncatedTree && (
        <Alert
          type="info"
          showIcon
          message={`Showing the first ${allUnits.length} of ${treeTotal} units. Search to narrow the view.`}
        />
      )}

      {counts.orphanedOfficers > 0 && (
        <Alert
          type="info"
          showIcon
          message={`${counts.orphanedOfficers} officer(s) are posted to units outside this view, and are not included in the counts above.`}
        />
      )}

      {isFlat ? (
        <Table<OrgUnitDto>
          {...flat.tableProps}
          columns={flatColumns}
          size="middle"
          onRow={(row) => ({ onClick: () => void setSelectedId(row.id) })}
          locale={{ emptyText: <EmptyState size="sm" title="No units match these filters." /> }}
        />
      ) : (
        <HierarchyTable<OrgTreeRow>
          rows={rows}
          ariaLabel="Organisation hierarchy"
          data-testid="org-hierarchy"
          loading={treeLoading}
          expansion={expansion}
          selectedKey={selectedId ?? undefined}
          onSelect={(row) => void setSelectedId(row.id)}
          onOpen={(row) => void setSelectedId(row.id)}
          tree={{
            title: 'Unit',
            width: 380,
            code: (row) => row.shortCode,
            name: (row) => (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span className={row.isActive ? '' : 'text-muted-foreground line-through'}>{row.name}</span>
                <ElevationMark elevation={row.elevation} actingAt={actingAtOf(row)} />
              </span>
            ),
          }}
          columns={dataColumns}
          rowActions={rowActions}
          empty={<EmptyState title="No org units in your scope yet." />}
        />
      )}

      {/*
        ⚠️ [P4-UX] AN OVERLAY, NOT A PANEL BELOW THE TABLE — AND THE MEASUREMENT IS WHY.
        ────────────────────────────────────────────────────────────────────────────────
        This used to be the fourth child of the page's `flex flex-col`, rendered UNDER the
        whole tree. Measured at the 1366×768 floor: the panel's top edge sat at y=684 in a
        768px viewport — 425px below the fold — with `main.scrollTop` still 0 and no
        `scrollIntoView`, no ref and no `focus()` anywhere on this page. The URL changed and
        the data loaded, so every assertion in `OrgTree.test.tsx` passed while the operator
        saw nothing at all and concluded the page was broken. It came into view only at
        2560×1440, which is why a wider (and taller) window appeared to "fix" it.

        A real side-by-side split is arithmetically impossible at the floor: `<main>` is
        1062px there, a 420px panel leaves 642, and the tree needs ~926.

        UNCONDITIONAL rather than a width switch. The defect is not width-dependent — the
        panel is below the fold at every rung — and a width-branching render would need
        `matchMedia`, which `vitest.setup.ts` stubs to `matches: false` forever, so the
        narrow branch would be the only one any test ever saw.

        AntD `Drawer` rather than the shadcn `Sheet`: `ui/sheet.tsx`'s own header reserves
        AntD's Drawer for data-dense content and the shell for shadcn, and `PlaceDialog` and
        `OfficerDrawer` already establish it here. It brings the focus trap, Escape and the
        mask with it — none of which a `position: fixed` div would have.
      */}
      <Drawer
        open={Boolean(selected)}
        onClose={() => void setSelectedId(null)}
        placement="right"
        width="min(920px, 92vw)"
        destroyOnHidden
        title={selected?.name}
        data-testid="org-unit-detail"
      >
        {/*
          The drawer portals to <body>, outside <main>, so `OrgUnitDetail`'s own
          `TwoColumnLayout` would find no container query ancestor and stay single-column
          for ever. One node restores it.
        */}
        <div className="@container">
          {selected && (
            <OrgUnitDetail
              unit={selected}
              counts={counts.countsFor(selected.id)}
              countsLoading={counts.isLoading}
              allUnits={allUnits}
              canWrite={canWrite}
              editRefusal={elevatedNodeRefusal(officer, selected.id)}
              canElevate={canElevate}
              canReparent={canReparent}
              onAddChild={() => setEditing({ mode: 'create', parent: selected })}
              onEdit={() => setEditing({ mode: 'edit', unit: selected })}
              onMove={() => setMoving(selected)}
              onSelectUnit={(unit) => void setSelectedId(unit.id)}
            />
          )}
        </div>
      </Drawer>

      {editing && (
        <OrgUnitDrawer
          open
          mode={editing.mode}
          unit={editing.mode === 'edit' ? editing.unit : undefined}
          parent={editing.parent}
          readOnly={
            !canWrite || (editing.mode === 'edit' && elevatedNodeRefusal(officer, editing.unit?.id) !== undefined)
          }
          onClose={() => setEditing(undefined)}
        />
      )}

      {moving && <MoveUnitModal unit={moving} allUnits={allUnits} onClose={() => setMoving(undefined)} />}
    </div>
  );
};

export default OrgTreePage;
